import { DrawerSheet, DrawerSheetTitle } from '../drawer-sheet';
import { BlockLoading } from '../fetch-status/block-loading';
import { TextTooltip } from '../text-tooltip';
import { Button } from '../ui/button';
import { UploadedImage } from '../uploaded-image';
import { InteractCloseIcon } from '@/client/assets';
import { LocalizedText } from '@/client/components/localized-text';
import { TradePanel } from '@/client/components/trade';
import { useAppSettingStore } from '@/client/store/app-store';
import { useTradeParamsStore } from '@/client/store/trade-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useMarketDetail } from '@/libs/react-sdk/api/use-market-detail';

type TradeSidebarProps = {
  className?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
};

export const TradeSidebar = ({ className, open, setOpen }: TradeSidebarProps) => {
  const marketAddress = useTradeParamsStore(state => state.marketAddress);
  const baseTokenAddress = useAppSettingStore(state => state.baseToken.address);
  const { data: market, isLoading } = useMarketDetail(api, { baseTokenAddress, marketAddress });
  if (!open) return null;
  if (!market || isLoading) return <BlockLoading />;
  return (
    <DrawerSheet
      open={open}
      setOpen={setOpen}
      className={cn(
        // Sheet renders at a fixed physical side='right' (see drawer-sheet/index.tsx;
        // Sheet's side variants are intentionally left physical, not RTL-mirrored —
        // see ui/sheet.tsx), so this border/shadow must stay physical to match.
        'md:p-3 md:border-l-.5 md:border-foreground/10 md:bg-[#F4F5F6]/80 md:dark:bg-background md:w-[407px] md:backdrop-blur-[25px] md:shadow-[-2px_0px_4px_0px] shadow-card/10',
        className
      )}
    >
      {/* Always-present accessible title (sr-only) so the dialog has a name on
          mobile too; the visible header below is hidden on phones. */}
      <DrawerSheetTitle className="sr-only">
        <LocalizedText i18n={market.nameI18n}>{market.marketName}</LocalizedText>
      </DrawerSheetTitle>
      <div className="gap-2 hidden md:flex items-center md:mb-3">
        <UploadedImage fileKey={market.marketPicUrl} className="size-5 rounded-1.5" />
        <TextTooltip
          tooltip={<LocalizedText i18n={market.nameI18n}>{market.marketName}</LocalizedText>}
          className="line-clamp-1 text-start flex-1"
        >
          <LocalizedText i18n={market.nameI18n}>{market.marketName}</LocalizedText>
        </TextTooltip>
        <Button
          size="icon"
          variant="ghost"
          aria-label="Close"
          className="transition-all duration-200 hover:scale-90 focus:scale-80"
          onClick={() => setOpen(false)}
        >
          <InteractCloseIcon className="!size-6 fill-foreground/40" />
        </Button>
      </div>
      <TradePanel tradeClassName="md:shadow-none md:border-none" />
    </DrawerSheet>
  );
};
